import React, { useEffect, useState } from 'react';
import { css } from '@emotion/react';

// components
import Banner from '@leafygreen-ui/banner';
import { Body } from '@leafygreen-ui/typography';

// api
import * as api from 'js/common/services/api';

// common styles
import { bodyParagraphStyles } from 'js/common/styles/multiFactorAuth';

// types
import { WebAuthnOktaMfaFactor, FactorType } from 'js/common/types/accountMultiFactorAuth';
import { Search } from 'js/auth/types/search';

// utils
import { strToBin, binToStr } from 'js/common/utils/multiFactorAuthUtils';

const errorBannerStyles = css({
  marginBottom: '14px',
  boxSizing: 'border-box',
});

interface VerifyWebAuthnFactorProps {
  webAuthnFactor: WebAuthnOktaMfaFactor;
  clientState?: Search;
  onSuccess: (response?) => void;
  isDeleteFlow?: boolean;
  navigatorCredentials?: CredentialsContainer;
}
interface CredentialDataOptions {
  clientData: string;
  authenticatorData: string;
  signatureData: string;
}

export default function VerifyWebAuthnFactor({
  webAuthnFactor,
  clientState,
  onSuccess,
  isDeleteFlow,
  navigatorCredentials = navigator.credentials,
}: VerifyWebAuthnFactorProps) {
  const [errorMessage, setErrorMessage] = useState('');
  const { stateToken } = clientState || {};
  const isAuthFlow = !!stateToken;

  const getCredentials = async (response) => {
    const { credentialId } = response.profile ?? response._embedded.factor.profile;
    const allowCredentials = [
      {
        type: 'public-key',
        id: strToBin(credentialId),
      },
    ];

    const challengeOptions = response._embedded.challenge ?? response._embedded.factor._embedded.challenge;
    const challengeStr =
      response._embedded.challenge?.challenge ?? response._embedded.factor._embedded.challenge.challenge;

    const publicKeyOptions: PublicKeyCredentialRequestOptions = {
      ...challengeOptions,
      challenge: strToBin(challengeStr),
      allowCredentials,
    };

    try {
      const credential: Credential | null = await navigatorCredentials.get({
        publicKey: { ...publicKeyOptions },
      });

      const { clientDataJSON, authenticatorData, signature } = (credential as PublicKeyCredential)
        ?.response as AuthenticatorAssertionResponse;

      return {
        clientData: binToStr(clientDataJSON),
        authenticatorData: binToStr(authenticatorData),
        signatureData: binToStr(signature),
      } as CredentialDataOptions;
    } catch (error) {
      console.log('Something went wrong while verifying the credentials', error);
      setErrorMessage('A server error has occurred. Please try again in a moment.');
    }
  };

  const WEBAUTHN_FACTOR_PARAMS = {
    factorType: FactorType.WebAuthn,
    factorId: webAuthnFactor.id,
  };

  const getChallengeNonce = async () => {
    let challengeNonce;

    try {
      if (isAuthFlow) {
        challengeNonce = await api.auth.verifyAuthMfa({
          ...WEBAUTHN_FACTOR_PARAMS,
          stateToken,
          clientState,
        });
      } else {
        challengeNonce = await api.accountMultiFactorAuth.verifyFactor({ ...WEBAUTHN_FACTOR_PARAMS }, isDeleteFlow);
      }

      return await getCredentials(challengeNonce);
    } catch (err) {
      console.error('Something went wrong while getting the credentials', err);
      setErrorMessage('A server error has occurred. Please try again in a moment.');
    }
  };

  const verifyChallenge = async ({ clientData, authenticatorData, signatureData }) => {
    let verificationResponse;

    try {
      if (isAuthFlow) {
        verificationResponse = await api.auth.verifyAuthMfa({
          ...WEBAUTHN_FACTOR_PARAMS,
          stateToken,
          clientState,
          clientData,
          authenticatorData,
          signatureData,
        });
      } else {
        verificationResponse = await api.accountMultiFactorAuth.verifyFactor(
          {
            ...WEBAUTHN_FACTOR_PARAMS,
            clientData,
            authenticatorData,
            signatureData,
          },
          isDeleteFlow
        );
      }

      return verificationResponse;
    } catch (err) {
      console.error('Something went wrong when verifying the challenge', err);
      setErrorMessage('A server error has occurred. Please try again in a moment.');
    }
  };

  useEffect(() => {
    sendWebAuthnChallenge();
  });

  const sendWebAuthnChallenge = async () => {
    try {
      const successResponse = await verifyChallenge((await getChallengeNonce()) as CredentialDataOptions);
      onSuccess(successResponse);
    } catch (err) {
      console.log('Something went wrong when verifying the challenge response', err);
      setErrorMessage('A server error has occurred. Please try again in a moment.');
    }
  };

  return (
    <>
      {errorMessage && (
        <Banner variant="danger" data-testid="error-banner" css={errorBannerStyles}>
          {errorMessage}
        </Banner>
      )}
      <Body css={bodyParagraphStyles}>Please follow instructions in your browser.</Body>
    </>
  );
}
